import {
  BoxGeometry,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  type Scene,
  TorusGeometry,
  Vector3,
} from 'three';
import type { DuelistSpec } from '../combat/DuelistChampion';
import type { DummySpec } from '../combat/TargetDummies';
import type { ZoneDef } from '../game/zones';
import { buildLane } from './laneBuilder';
import { loadMaterials } from './materials';
import { mergeStaticMeshes } from './meshMerge';
import { generateBoxUVs, makeSpire, makeSurfPrism } from './ramps';

export interface BuiltLevel {
  /** Stable id for records/leaderboards. */
  mapId: string;
  /** Meshes the player collides with (fed to LevelCollider). */
  colliders: Mesh[];
  /** Player spawn point (capsule feet). */
  spawn: Vector3;
  /** Initial view yaw (radians) — face down the lane, not away from it. */
  spawnYaw: number;
  /** Run zones. Empty = untimed map (the timer never arms). */
  zones: ZoneDef[];
  /** Y coordinate of the kill plane — player respawns when they fall below it. */
  killY?: number;
  /** Optional per-frame animation hook (real seconds), e.g. spinning decor. */
  update?: (dtSeconds: number) => void;
  /** Combat content (specs 0026/0028). Absent on practice maps — combat stays inert. */
  combat?: { dummies: DummySpec[]; duelist?: DuelistSpec };
}

/**
 * "The Playground" — an untimed free-surf jungle (owner redesign, 2026-06-16).
 * You spawn high above a lava floor onto a clean intro ramp, then drop into a
 * dense, multi-level field of varied surf ramps that fills the whole volume:
 * hundreds of prisms and curved lanes at every altitude, scattered with transfer
 * gaps between them so the play is *pick a line and air-strafe across to the
 * next one*. Ramps point every direction, narrow and steepen the lower you go,
 * and cascade outward as they descend — so there are always many options
 * everywhere, at every level, down to a base plaza that teleports you back to the
 * top for an endless loop.
 *
 * The field is generated deterministically (seeded jittered lattice) so it's
 * stable + sim-testable, and merged per-colour into a handful of draw calls
 * (`mergeStaticMeshes`). Untimed (only the teleport loop); miss every ramp and
 * the lava floor resets you to the top. Surfability is sim-tested
 * (TestLevel.test.ts). Curve/tier vocabulary: docs/reference/MAP_DESIGN.md.
 */

/** Start platform height; the intro ramp sits just below + ahead of it. */
const START_Y = 9200;
const INTRO_POS = new Vector3(0, 8900, -1700);
const INTRO = { width: 1200, angleDeg: 55, declineDeg: 10, length: 4200, headingDeg: 180 };

/** Vertical bands and radial rings the scatter field fills (cascading outward). */
const FIELD_TOP_Y = 8300;
const FIELD_LAYERS = 8;
const LAYER_DROP = 880;
const RINGS = [1700, 3500, 5300, 7000];
/** Target arc spacing between ramps on a ring → the transfer gap. */
const SLOT_SPACING = 2600;

/** Tiny deterministic PRNG (mulberry32) — same seed ⇒ same playground. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export async function buildTestLevel(scene: Scene): Promise<BuiltLevel> {
  const materials = await loadMaterials();
  const rampMaterial = materials.ramp;
  const platformMaterial = materials.platform;

  // Bright skate-park palette so the jungle reads as a playful gym. Each hue is a
  // tinted clone of the ramp material (keeps grain + DoubleSide), cached so the
  // per-colour merge collapses the whole field to one mesh per colour.
  const PLAY_COLORS = [
    0xff6b6b, 0x4dabf7, 0xffd43b, 0x69db7c, 0xff922b, 0xb197fc, 0x3bc9db, 0xf783ac,
  ];
  const tintCache = new Map<number, MeshStandardMaterial>();
  const hue = (i: number): MeshStandardMaterial => {
    const hex = PLAY_COLORS[
      ((i % PLAY_COLORS.length) + PLAY_COLORS.length) % PLAY_COLORS.length
    ] as number;
    let m = tintCache.get(hex);
    if (!m) {
      m = (rampMaterial as MeshStandardMaterial).clone();
      m.color.setHex(hex);
      tintCache.set(hex, m);
    }
    return m;
  };

  const colliders: Mesh[] = []; // ground (kept separate) + the merged field
  const toMerge: Mesh[] = []; // ramps + platforms, collapsed per-material at the end

  // Ground — "the floor is lava": touch it and you respawn to the top.
  const groundGeometry = new PlaneGeometry(40000, 40000);
  groundGeometry.computeVertexNormals();
  generateBoxUVs(groundGeometry, 512);
  const ground = new Mesh(groundGeometry, materials.ground);
  ground.rotation.x = -Math.PI / 2;
  ground.castShadow = false;
  ground.receiveShadow = true;
  scene.add(ground);
  colliders.push(ground);

  /** Place a straight surf prism and queue it for the merge. */
  const placePrism = (
    pos: Vector3,
    headingDeg: number,
    declineDeg: number,
    opts: { length: number; width: number; angleDeg: number },
    mat: MeshStandardMaterial,
  ): void => {
    const mesh = new Mesh(makeSurfPrism(opts), mat);
    mesh.rotation.order = 'YXZ';
    mesh.rotation.y = (headingDeg * Math.PI) / 180;
    mesh.rotation.x = (declineDeg * Math.PI) / 180;
    mesh.position.copy(pos);
    mesh.castShadow = false; // hundreds of ramps — shadows are a disproportionate cost
    mesh.receiveShadow = true;
    scene.add(mesh);
    toMerge.push(mesh);
  };

  const platform = (x: number, y: number, z: number, size = 320): void => {
    const p = new Mesh(new BoxGeometry(size, 48, size), platformMaterial);
    p.position.set(x, y, z);
    scene.add(p);
    toMerge.push(p);
  };

  // Start platform + the clean intro ramp you walk off it onto (faces -z).
  platform(INTRO_POS.x, START_Y, INTRO_POS.z + 200, 900);
  placePrism(
    INTRO_POS,
    INTRO.headingDeg,
    INTRO.declineDeg,
    { length: INTRO.length, width: INTRO.width, angleDeg: INTRO.angleDeg },
    hue(1),
  );

  // ── The scatter field — many ramps, every direction, every level ────────────
  // A jittered cylindrical lattice: rings × angular slots × vertical layers. Each
  // cell gets one ramp (mostly straight prisms, some curved lanes), jittered in
  // position, pointing roughly outward with wide random spread, narrowing and
  // steepening with depth. Cell spacing IS the transfer gap.
  const rng = mulberry32(0x504c4159); // "PLAY"
  let hueIdx = 2;

  for (let layer = 0; layer < FIELD_LAYERS; layer++) {
    const depth = layer / (FIELD_LAYERS - 1); // 0 top → 1 bottom
    const layerY = FIELD_TOP_Y - layer * LAYER_DROP;
    const brick = layer * 0.5; // offset alternate layers so they don't stack in columns

    for (const radius of RINGS) {
      const slots = Math.max(4, Math.round((2 * Math.PI * radius) / SLOT_SPACING));
      for (let s = 0; s < slots; s++) {
        const ringAngle = ((s + brick) / slots) * Math.PI * 2;
        const x = Math.sin(ringAngle) * radius + (rng() - 0.5) * 520;
        const z = Math.cos(ringAngle) * radius + (rng() - 0.5) * 520;
        const y = layerY + (rng() - 0.5) * 460;
        const pos = new Vector3(x, y, z);

        // Point roughly outward (so the field cascades down-and-out) with a wide
        // random spread, so from any ramp several neighbours are reachable.
        const headingDeg = (ringAngle * 180) / Math.PI + (rng() - 0.5) * 220;
        const angleDeg = 53 + depth * 5 + rng() * 2; // steeper lower down (T1→T3)
        const declineDeg = 9 + depth * 4 + rng() * 2;
        const width = 760 + rng() * 520;
        const length = 1900 + rng() * 1100;
        const mat = hue(hueIdx++);

        if (rng() < 0.2) {
          // Curved lane variant — a short banked sweep or S, for line variety.
          const turn = (rng() < 0.5 ? -1 : 1) * (24 + rng() * 50);
          const run = buildLane(
            {
              width,
              angleDeg,
              declineDeg,
              segments: [{ length: length * 0.45 }, { length: length * 0.55, turnDeg: turn }],
            },
            pos,
            headingDeg,
            mat,
          );
          for (const mesh of run.meshes) {
            mesh.castShadow = false;
            mesh.receiveShadow = true;
            scene.add(mesh);
            toMerge.push(mesh);
          }
        } else {
          placePrism(pos, headingDeg, declineDeg, { length, width, angleDeg }, mat);
        }
      }
    }
  }

  // A few spire landmarks scattered at mid heights, each with a drop-in perch.
  const spireSpots: [number, number, number, number][] = [
    [2600, 5200, 2600, 6],
    [-3800, 3800, 2200, 6],
    [3200, 2600, -4200, 5],
    [-2400, 6400, -3000, 6],
  ];
  spireSpots.forEach(([sx, sy, sz, hi], i) => {
    const spire = new Mesh(
      makeSpire({ baseRadius: 560, topRadius: 110, height: 1400 }),
      hue(hi + i),
    );
    spire.position.set(sx, sy, sz);
    spire.castShadow = false;
    spire.receiveShadow = true;
    scene.add(spire);
    toMerge.push(spire);
    platform(sx + 760, sy + 1500, sz, 280);
  });

  // Base plaza — a wide deck under the field's centre to land on, above the lava.
  const PLAZA_Y = 1500;
  platform(0, PLAZA_Y, 0, 4200);

  // Collapse the field + platforms into one mesh per material (draw-call win; the
  // BVH re-merges for collision, so this is free for physics).
  colliders.push(...mergeStaticMeshes(scene, toMerge));

  // ── Hoops — a few colourful gateways for flair (visual only) ────────────────
  const hoop = (color: number, pos: Vector3, radius: number): void => {
    const ring = new Mesh(
      new TorusGeometry(radius, radius * 0.06, 16, 40),
      new MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.4, roughness: 0.4 }),
    );
    ring.position.copy(pos);
    ring.rotation.x = Math.PI / 2;
    ring.castShadow = false;
    scene.add(ring);
  };
  hoop(0xffd43b, new Vector3(0, START_Y + 520, INTRO_POS.z), 1000);
  hoop(0x4dabf7, new Vector3(3500, 5400, 0), 820);
  hoop(0x69db7c, new Vector3(0, PLAZA_Y + 900, 0), 1200);

  // ── Teleport loop — coast onto the base plaza, warp back to the top ─────────
  const teleSize = new Vector3(3600, 1500, 3600);
  const teleCenter = new Vector3(0, PLAZA_Y + 770, 0);
  const teleMarker = new Mesh(
    new BoxGeometry(teleSize.x, teleSize.y, teleSize.z),
    new MeshStandardMaterial({
      color: 0xab47bc,
      emissive: 0xab47bc,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.1,
    }),
  );
  teleMarker.position.copy(teleCenter);
  scene.add(teleMarker); // visual only — never a collider

  const spawn = new Vector3(INTRO_POS.x, START_Y + 40, INTRO_POS.z + 200);
  const zones: ZoneDef[] = [
    { kind: 'teleport', center: teleCenter, size: teleSize, dest: spawn.clone() },
  ];

  // Untimed: only the teleport effect zone (the timer never arms). Spawn on the
  // top platform facing the intro ramp (-z); killY just above the lava floor so
  // only the ground trips it (every ramp floats well above).
  return {
    mapId: 'gym_falline',
    colliders,
    spawn,
    spawnYaw: 0, // faces -z, down the intro ramp
    zones,
    killY: 40,
  };
}
